import { Drawer, Button, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useIntl } from 'react-intl';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';
import { ComboBox, Icon, LoadingSpinner, WorkflowMessage } from '@verantos-dev/shared-components';
import { DomainType } from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { ComboBoxDropdownListItem } from '../hooks/useCharacteristicsConceptGroupsMapping';
import { useCustomConceptGroupsDialog } from '../hooks/useCustomConceptGroupsDialog';

interface CustomConceptGroupsDialogProps {
  open: boolean;
  onClose: () => void;
  domain: DomainType;
  availableConceptGroups: ComboBoxDropdownListItem[];
  selectedConceptGroups: ComboBoxDropdownListItem[];
  onApplySelection: (selectedGroups: ConceptGroup[]) => void;
  isLoading?: boolean;
  error?: string;
  /** Currently-pinned dataset release id; forwarded to the details dialog
   * for the prevalence preview panel. */
  datasetReleaseId?: string | null;
  /** User-facing dataset release name; forwarded alongside the id. */
  datasetReleaseDisplayName?: string | null;
}

export const CustomConceptGroupsDialog: React.FC<CustomConceptGroupsDialogProps> = ({
  open,
  onClose,
  domain,
  availableConceptGroups,
  selectedConceptGroups,
  onApplySelection,
  isLoading = false,
  error,
  datasetReleaseId = null,
  datasetReleaseDisplayName = null,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const {
    localSelectedGroups,
    selectedConceptGroupForDetails,
    canApply,
    domainDisplayName,
    comboBoxOptions,
    handleComboBoxChange,
    handleApply,
    handleCancel,
    handleActionButtonClick,
    handleCloseConceptGroupDetails,
  } = useCustomConceptGroupsDialog({
    open,
    domain,
    availableConceptGroups,
    selectedConceptGroups,
    onApplySelection,
    onClose,
    isLoading,
  });

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCancel}
        PaperProps={{
          sx: {
            height: '100vh',
            width: '476px',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">
            {intl.formatMessage(
              { id: 'projects.cohortBuilder.cohortCharacteristics.customizeDialog.title' },
              { domain: domainDisplayName }
            )}
          </Typography>
          <Box
            onClick={handleCancel}
            sx={{
              cursor: 'pointer',
              borderRadius: '4px',
              padding: theme.spacing(1),
              height: 32,
              width: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Icon id="close-icon" icon="close" />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 6, flex: 1, overflow: 'auto' }}>
          {error ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <WorkflowMessage
                id="customize-conditions-error"
                title="Error"
                message="Unable to load customized conditions. Try reloading the page."
              />
            </Box>
          ) : isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <LoadingSpinner
                id="customize-conditions-loading"
                title={`Loading ${domainDisplayName}`}
              />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {intl.formatMessage({
                  id: 'projects.cohortBuilder.cohortCharacteristics.customizeDialog.description',
                })}
              </Typography>

              <Typography variant="body2" sx={{ mb: 4 }}>
                {intl.formatMessage(
                  {
                    id: 'projects.cohortBuilder.cohortCharacteristics.customizeDialog.selectionCount',
                  },
                  { selected: localSelectedGroups.length, total: 10 }
                )}
              </Typography>

              <ComboBox
                id="concept-groups-combobox"
                label="Concept groups"
                placeholder="Select concept groups"
                options={comboBoxOptions}
                value={localSelectedGroups}
                onChange={handleComboBoxChange}
                onActionButtonClick={handleActionButtonClick}
                multiple={true}
                limitTags={3}
                width={428}
                open={true}
                disableCloseOnSelect={true}
                showCheckbox={true}
                showIcon={true}
                selectedItemsOnTop={true}
                filterSelectedOptions={false}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                  },
                }}
              />

              {localSelectedGroups.length >= 10 && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                  Maximum of 10 concept groups reached
                </Typography>
              )}

              {localSelectedGroups.length === 1 && (
                <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
                  At least 1 concept group must be selected
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Actions */}
        <Box
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={handleCancel}
            variant="text"
            sx={{
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={!canApply || isLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 3,
            }}
          >
            Apply selection
          </Button>
        </Box>
      </Drawer>

      {selectedConceptGroupForDetails && (
        <ConceptGroupDetailsDialog
          onClose={handleCloseConceptGroupDetails}
          conceptGroupId={selectedConceptGroupForDetails}
          datasetReleaseId={datasetReleaseId}
          datasetReleaseDisplayName={datasetReleaseDisplayName}
        />
      )}
    </>
  );
};
